import { formatDate as formatLocaleDate, formatTimeOfDay } from '@hqo/date-time/core';

export const formatDate = (value: string, locale: string, timezone?: string) => {
  const timeZone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const eventDateMonth = new Date(value).toLocaleString(locale, { month: 'short', timeZone });
  const eventDateMonthNum = new Date(value).toLocaleString(locale, { month: 'numeric', timeZone });
  const eventDateDay = new Date(value).toLocaleString(locale, { day: 'numeric', timeZone });
  const eventDateMonthLong = new Date(value).toLocaleString(locale, { month: 'long', timeZone });
  const eventDateYear = new Date(value).toLocaleString(locale, { year: 'numeric', timeZone });
  const eventWeekDay = new Date(value).toLocaleString(locale, { weekday: 'long', timeZone });
  const formatEventStartTime = formatTimeOfDay(new Date(value), { locale, timezone: timeZone });
  // Whole localized date (weekday + day/month/year in the locale's own field ORDER). Prefer this over
  // composing the parts by hand: `${weekday}, ${month} ${day}, ${year}` is US order and reads wrong
  // in every other locale (French wants `dimanche 13 septembre 2026`, not `septembre 13, 2026`).
  const formattedFullDate = formatLocaleDate(value, { locale, timezone: timeZone, dateStyle: 'full' });

  return {
    eventDateMonth,
    eventDateMonthNum,
    eventDateDay,
    eventDateMonthLong,
    eventDateYear,
    formatEventStartTime,
    formattedFullDate,
    eventWeekDay,
  };
};

export const bookingDate = (startDate: string, locale: string, timezone?: string): string => {
  const { eventWeekDay, eventDateMonth, eventDateDay, eventDateYear } = formatDate(startDate, locale, timezone);
  return `${eventWeekDay}, ${eventDateMonth} ${eventDateDay}, ${eventDateYear}`;
};

// Locale-correct long date (long month + day + year, no weekday) in the locale's own field ORDER —
// en-US `September 13, 2026`, fr-FR `13 septembre 2026`. Use this for a selected calendar day instead
// of `moment(value).format('MMMM DD, YYYY')`, which is hard-wired to US month-day-year order.
// Defaults to UTC so a bare `YYYY-MM-DD` calendar day isn't shifted to the previous day in a negative
// offset (`2026-09-13` parses as UTC midnight, so it must be read back in UTC to stay the 13th).
export const formatLongDate = (value: string | Date, locale: string, timezone = 'UTC'): string =>
  formatLocaleDate(value, { locale, timezone, dateStyle: 'long' });

// Short month + day in the locale's own field order -- en-US `Sep 24`, fr-FR `24 sept.`. Do not
// compose it from `formatDate`'s `eventDateMonth` + `eventDateDay`: that hard-wires US order, the
// trap `formattedFullDate` above documents. No year, matching the add-on notice line.
export const formatMonthDay = (value: string | Date, locale: string, timezone?: string): string =>
  new Date(value).toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

export const getFormattedEventDuration = (
  startDate: string,
  endDate: string,
  locale: string,
  timezone?: string,
): string => {
  const timeZone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const eventStartTime = formatTimeOfDay(new Date(startDate), { locale, timezone: timeZone });
  const eventEndTime = formatTimeOfDay(new Date(endDate), { locale, timezone: timeZone });

  return `${eventStartTime} - ${eventEndTime}`;
};

export const getFormattedDate = (date: Date, format: string, locale: string): string => {
  const mm = date.toLocaleDateString(locale, { month: '2-digit' });
  const dd = date.toLocaleDateString(locale, { day: '2-digit' });
  const yy = date.getFullYear();
  if (format === 'YYYY-MM-DD') {
    return `${yy}-${mm}-${dd}`;
  }
  if (format === 'MMMM YYYY') {
    return date.toLocaleString(locale, { month: 'long', year: 'numeric' });
  }

  return '';
};

export const transformToSortedDatesList = (datesString: string): Array<string> => {
  const formattedStartDates = datesString?.length > 0 ? datesString?.split(',') : [];

  return formattedStartDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
};

// Largest-unit-that-fits divisions for Intl.RelativeTimeFormat, in ascending order — mirrors the
// standard MDN recipe. Weeks use the average Gregorian month/(4 + 1/3) so a run of 4 weeks doesn't
// get relabeled "1 month" a few days early.
const RELATIVE_TIME_DIVISIONS: Array<{ amount: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { amount: 60, unit: 'seconds' },
  { amount: 60, unit: 'minutes' },
  { amount: 24, unit: 'hours' },
  { amount: 7, unit: 'days' },
  { amount: 4.34524, unit: 'weeks' },
  { amount: 12, unit: 'months' },
  { amount: Number.POSITIVE_INFINITY, unit: 'years' },
];

// RES-999: "Booked {time} ago" on the routed booking-details page. Uses Intl.RelativeTimeFormat —
// already the app's pattern for locale-aware date text (see formatDate above, all via
// toLocaleString) — rather than a bespoke set of pluralized "x_days_ago" i18n keys, so this needs
// zero new per-locale translation strings beyond the single "{time}" wrapper key.
export const getRelativeTimeAgo = (value: string | Date, locale: string): string => {
  let durationInSeconds = (new Date(value).getTime() - Date.now()) / 1000;

  const relativeTimeFormat = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  for (const division of RELATIVE_TIME_DIVISIONS) {
    if (Math.abs(durationInSeconds) < division.amount) {
      return relativeTimeFormat.format(Math.round(durationInSeconds), division.unit);
    }
    durationInSeconds /= division.amount;
  }

  return relativeTimeFormat.format(Math.round(durationInSeconds), 'years');
};
